import { useEffect, useRef, useState, type FC } from 'react';
import toast from 'react-hot-toast';
import { BookOpen, ImagePlus, Send, X } from 'lucide-react';
import {
  apiErrorMessage,
  fetchTeacherChatApi,
  resolveMediaUrl,
  sendTeacherMessageApi,
  uploadStudentFileApi,
  type ChatMessageView,
} from '../api/client';
import { compressImage } from '../lib/compressImage';
import { formatDateTime } from '../lib/format';
import { t } from '../i18n';

/**
 * "Ustozga savol" — dars sahifasidan.
 *
 * Savolga dars nomi oʻzi qoʻshiladi: ustoz Telegramda va panelda
 * "📖 Dars: …" ni koʻradi, oʻquvchi "qaysi dars" deb tushuntirib
 * oʻtirmaydi. Ustoz javobi botga keladi (Mini App yopiq boʻlsa ham);
 * shu yerda esa butun yozishma koʻrinadi.
 */
export const AskTeacherSheet: FC<{
  lessonId: string;
  lessonTitle: string;
  onClose: () => void;
}> = ({ lessonId, lessonTitle, onClose }) => {
  const [teacher, setTeacher] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageView[] | null>(null);
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const r = await fetchTeacherChatApi();
      setTeacher(r.teacher.fullName);
      setMessages(r.messages);
    } catch {
      // Tarix kelmasa ham yozish mumkin.
      setMessages([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages?.length]);

  const send = async () => {
    if (!text.trim() && !photo) return;
    setSending(true);
    try {
      let attachment: string | undefined;
      if (photo) {
        const uploaded = await uploadStudentFileApi(await compressImage(photo));
        attachment = uploaded.url;
      }
      await sendTeacherMessageApi({
        text: text.trim() || undefined,
        attachment,
        lessonId,
      });
      toast.success(t('Yuborildi. Ustoz javobi botga keladi.'));
      setText('');
      setPhoto(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('Yuborib boʻlmadi.')));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('Ustozga savol')}
      onClick={onClose}
    >
      <div className="sheet ask-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" aria-hidden="true" />
        <div className="spread" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="text-base" style={{ margin: 0 }}>
              {t('Ustozga savol')}
            </h2>
            {teacher && <span className="text-xs muted">{teacher}</span>}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label={t('Yopish')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="ask-thread">
          {messages === null ? (
            <div className="skeleton" style={{ height: 80 }} />
          ) : messages.length === 0 ? (
            <p className="text-sm muted">
              {t('Savolingizni yozing — ustozingiz javobi botga keladi.')}
            </p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`ask-bubble${m.sender === 'STUDENT' ? ' is-mine' : ''}`}
              >
                {m.lesson && (
                  <span className="ask-bubble__lesson">
                    <BookOpen size={11} aria-hidden="true" /> {m.lesson.title}
                  </span>
                )}
                {m.attachmentUrl && !/\.pdf($|\?)/i.test(m.attachmentUrl) && (
                  <img
                    className="ask-bubble__image"
                    src={resolveMediaUrl(m.attachmentUrl) ?? undefined}
                    alt={t('Rasm')}
                  />
                )}
                {m.text && <p className="ask-bubble__text">{m.text}</p>}
                <span className="ask-bubble__time">
                  {formatDateTime(m.createdAt)}
                </span>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <span className="chip" style={{ marginBottom: 'var(--space-2)' }}>
          <BookOpen size={12} aria-hidden="true" /> {lessonTitle}
        </span>
        <textarea
          className="input"
          style={{ minHeight: 90 }}
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('Savolingiz…')}
          aria-label={t('Savolingiz…')}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />
        <div className="row ask-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={sending}
          >
            <ImagePlus size={15} aria-hidden="true" />
            {photo ? t('Rasm tanlandi') : t('Rasm')}
          </button>
          {photo && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setPhoto(null)}
              aria-label={t('Rasmni olib tashlash')}
            >
              <X size={14} />
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => void send()}
            disabled={sending || (!text.trim() && !photo)}
          >
            <Send size={15} aria-hidden="true" />
            {sending ? t('Yuborilmoqda…') : t('Yuborish')}
          </button>
        </div>
      </div>
    </div>
  );
};
